import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import FetchData from '../fetch-api/Apifetch';
import { ALL_LIST_EMPLOYEE, LIST_REPORT, LIST_EMPLOYEEDEVICE, LIST_ADMINS_EMPLOYEEDEVICE, GET_MANAGER_TEAM } from '../utils/ApiRoute';
import { EncryptData } from '../utils/Constants';
import { EmpDropdowndash } from '../split/Keystroke/EmpDropdown';
import { FaChevronDown } from "react-icons/fa";

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
    const [empList, setEmpList] = useState([]);
    const [deviceList, setDeviceList] = useState([]);
    const [empLoading, setEmpLoading] = useState(false);
    const [deviceLoading, setDeviceLoading] = useState(false);

    // Always read fresh from localStorage so stale closures do not bite us
    const getUser = () => {
        try { return JSON.parse(localStorage.getItem('token')); } catch { return null; }
    };

    const [user, setUser] = useState(getUser);

    const [selectedEmpId, setSelectedEmpId] = useState(() => {
        return localStorage.getItem('SearchUser') || user?.emp_id || null;
    });
    const [selectedDeviceId, setSelectedDeviceId] = useState(() => {
        return localStorage.getItem('dev_id') || '0';
    });

    // ─── Sync user state with localStorage ────────────────────────────────────
    useEffect(() => {
        const handleStorageChange = () => {
            const currentUser = getUser();
            setUser(currentUser);
            // Also sync IDs if they are missing but available in new user object
            if (currentUser) {
                const searchUser = localStorage.getItem('SearchUser');
                if (!searchUser) {
                    setSelectedEmpId(currentUser.emp_id);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // ─── Permission Check ──────────────────────────────────────────────────
    const hasEmployeeAccess = useCallback((currentUser) => {
        if (!currentUser) return false;
        if (currentUser.type == 1 || localStorage.getItem('userType') === '1') return true;
        const roleTitle = (currentUser?.role_access_data?.title || currentUser?.role || "").toLowerCase();
        if (roleTitle.includes("admin") || roleTitle.includes("manager") || roleTitle.includes("supervisor") || roleTitle.includes("lead")) return true;

        const rawMenu = currentUser?.role_access_data?.menu || currentUser?.role_access_data || [];
        if (Array.isArray(rawMenu)) {
            return rawMenu.some(p => {
                const lp = (typeof p === 'string' ? p : '').toLowerCase();
                return lp.includes("/employee") || lp.includes("/application-track") || lp.includes("/url-track") || lp.includes("/screen-shots") || lp.includes("/task-report") || lp.includes("/overall-employee-report") || lp.includes("/keystroke");
            });
        } else if (typeof rawMenu === 'object' && rawMenu !== null) {
            return Object.keys(rawMenu).some(key => {
                const k = key.toLowerCase();
                const hasPerm = rawMenu[key]?.read === true || rawMenu[key]?.view === true;
                return hasPerm && (k.includes("/employee") || k.includes("/application-track") || k.includes("/url-track") || k.includes("/screen-shots") || k.includes("/task-report") || k.includes("/overall-employee-report") || k.includes("/keystroke"));
            });
        }
        return false;
    }, []);

    // ─── Load Employees ───────────────────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        const currentUser = getUser();
        if (!currentUser) {
            console.warn('[GlobalContext] loadEmployees: no user in localStorage, skipping.');
            setEmpList([]);
            return;
        }

        setEmpLoading(true);
        try {
            const cryptoKey = JSON.parse(localStorage.getItem('crypto_key'));
            const isSuperAdmin = currentUser?.type == 1 || localStorage.getItem('userType') === '1' || (currentUser?.role_access_data?.title || '').toLowerCase() === 'super admin';

            let data = [];

            if (isSuperAdmin) {
                // Super Admin sees all employees across organization
                console.log('[GlobalContext] Fetching all employees for Super Admin');
                const res = await FetchData(ALL_LIST_EMPLOYEE + '?scope=all', 'GET', null, true, false);
                if (Array.isArray(res?.data)) {
                    data = res.data;
                }
            } else {
                // Non-super admins (Managers, Supervisors, Team Leads, Employees) see only themselves and their team members
                const userEmpId = currentUser.emp_id;
                const userId = currentUser.id;

                const empListMap = new Map();

                const addEmp = (raw) => {
                    if (!raw) return;
                    const id = raw.id ?? raw.EmployeeId;
                    const emp_id = raw.emp_id ?? raw.EmployeeCode;
                    const name = raw.name || raw.EmployeeName || raw.emp_name || '';

                    // Match if already present by either numeric ID or string code
                    let existingKey = null;
                    for (const [key, val] of empListMap.entries()) {
                        const vId = val.id ?? val.EmployeeId;
                        const vCode = val.emp_id ?? val.EmployeeCode;
                        const idMatch = id && vId && String(id) === String(vId);
                        const codeMatch = emp_id && vCode && String(emp_id).toLowerCase() === String(vCode).toLowerCase();
                        if (idMatch || codeMatch) {
                            existingKey = key;
                            break;
                        }
                    }

                    const formatted = {
                        ...raw,
                        id: id || (existingKey ? empListMap.get(existingKey).id : undefined),
                        EmployeeId: id || (existingKey ? empListMap.get(existingKey).EmployeeId : undefined),
                        emp_id: emp_id || (existingKey ? empListMap.get(existingKey).emp_id : undefined),
                        EmployeeCode: emp_id || (existingKey ? empListMap.get(existingKey).EmployeeCode : undefined),
                        name: name || (existingKey ? empListMap.get(existingKey).name : ''),
                        EmployeeName: name || (existingKey ? empListMap.get(existingKey).EmployeeName : ''),
                        email: raw.email || raw.EmployeeEmail || (existingKey ? empListMap.get(existingKey).email : ''),
                        EmployeeEmail: raw.email || raw.EmployeeEmail || (existingKey ? empListMap.get(existingKey).EmployeeEmail : ''),
                        emp_image: raw.emp_image || raw.EmployeeImage || (existingKey ? empListMap.get(existingKey).emp_image : ''),
                        EmployeeImage: raw.emp_image || raw.EmployeeImage || (existingKey ? empListMap.get(existingKey).EmployeeImage : ''),
                    };

                    if (existingKey) {
                        empListMap.set(existingKey, { ...empListMap.get(existingKey), ...formatted });
                    } else {
                        const newKey = String(id || emp_id || Math.random());
                        empListMap.set(newKey, formatted);
                    }
                };

                // Add logged-in user themselves
                addEmp({
                    id: userId,
                    EmployeeId: userId,
                    emp_id: userEmpId,
                    EmployeeCode: userEmpId,
                    name: currentUser.name || currentUser.username || '',
                    EmployeeName: currentUser.name || currentUser.username || '',
                    email: currentUser.email || '',
                    EmployeeEmail: currentUser.email || '',
                    emp_image: currentUser.emp_image || '',
                    EmployeeImage: currentUser.emp_image || '',
                    depart_id: currentUser.depart_id,
                    subdept_id: currentUser.subdept_id,
                    role_id: currentUser.role_id,
                });

                // 1. Fetch team members via GET_MANAGER_TEAM if user has an ID
                if (userId) {
                    try {
                        const teamRes = await FetchData(GET_MANAGER_TEAM + userId, 'GET', null, true, false);
                        if (teamRes?.status === 'success') {
                            if (teamRes.manager) {
                                addEmp(teamRes.manager);
                            }
                            if (Array.isArray(teamRes.team_members)) {
                                teamRes.team_members.forEach(member => addEmp(member));
                            }
                        }
                    } catch (err) {
                        console.warn('[GlobalContext] GET_MANAGER_TEAM fetch error:', err);
                    }
                }

                // 2. Fetch reporting employees via LIST_REPORT
                if (userEmpId) {
                    try {
                        const encryptedId = EncryptData(userEmpId, cryptoKey);
                        const repRes = await FetchData(
                            LIST_REPORT + `?emp_id=${encodeURIComponent(encryptedId)}&type=${currentUser.type}`,
                            'GET',
                            null,
                            true,
                            false
                        );
                        if (repRes?.data) {
                            if (repRes.data.employee) {
                                addEmp(repRes.data.employee);
                            }
                            if (Array.isArray(repRes.data.reporting_employees)) {
                                repRes.data.reporting_employees.forEach(member => addEmp(member));
                            }
                        }
                    } catch (err) {
                        console.warn('[GlobalContext] LIST_REPORT fetch error:', err);
                    }
                }

                data = Array.from(empListMap.values());
            }

            if (Array.isArray(data) && data.length > 0) {
                setEmpList(data);
                console.log(`[GlobalContext] Loaded ${data.length} employees for role scope.`);

                // Check if current selectedEmpId exists in this scoped list; if not, reset to current user
                const currentSelected = localStorage.getItem('SearchUser') || selectedEmpId;
                const isFound = data.some(
                    emp => String(emp.emp_id) === String(currentSelected) ||
                           String(emp.id) === String(currentSelected) ||
                           String(emp.EmployeeCode) === String(currentSelected) ||
                           String(emp.EmployeeId) === String(currentSelected)
                );
                if (!isFound && currentUser?.emp_id) {
                    console.log('[GlobalContext] Resetting selected employee to current user:', currentUser.emp_id);
                    setSelectedEmpId(currentUser.emp_id);
                    localStorage.setItem('SearchUser', currentUser.emp_id);
                }
            } else {
                console.warn('[GlobalContext] Employee list is empty or invalid:', data);
                setEmpList([]);
            }
        } catch (err) {
            console.error('[GlobalContext] loadEmployees failed:', err?.message || err);
            setEmpList([]);
        } finally {
            setEmpLoading(false);
        }
    }, []);

    // ─── Load Devices ─────────────────────────────────────────────────────────
    const loadDevices = useCallback(async (empId, isEmpChange = false) => {
        const currentUser = getUser();
        if (!currentUser || !empId) {
            setDeviceList([]);
            return;
        }

        setDeviceLoading(true);
        try {
            const cryptoKey = JSON.parse(localStorage.getItem('crypto_key'));
            const encryptedId = EncryptData(empId, cryptoKey);
            const isPrivileged = hasEmployeeAccess(currentUser);
            const endpoint = (currentUser.type == 1 || isPrivileged || empId !== currentUser.emp_id)
                ? LIST_ADMINS_EMPLOYEEDEVICE
                : LIST_EMPLOYEEDEVICE;

            console.log('[GlobalContext] Fetching devices for emp:', empId, 'from:', endpoint);
            const res = await FetchData(
                endpoint + `?emp_id=${encodeURIComponent(encryptedId)}`,
                'GET', null, true, false
            );
            console.log('[GlobalContext] Device response:', res);

            const devices = Array.isArray(res?.data) ? res.data : [];
            setDeviceList(devices);

            if (isEmpChange) {
                setSelectedDeviceId('0');
                localStorage.setItem('dev_id', '0');
            } else {
                const cachedDev = localStorage.getItem('dev_id');
                const isCachedValid = cachedDev && cachedDev !== '0' && devices.some(d => d.device_id === cachedDev);
                if (isCachedValid) {
                    setSelectedDeviceId(cachedDev);
                } else {
                    setSelectedDeviceId('0');
                    localStorage.setItem('dev_id', '0');
                }
            }
        } catch (err) {
            console.error('[GlobalContext] loadDevices failed:', err?.message || err);
            setDeviceList([]);
        } finally {
            setDeviceLoading(false);
        }
    }, [hasEmployeeAccess]);

    // ─── React to User/Auth Changes ───────────────────────────────────────────
    useEffect(() => {
        if (user) {
            console.log('[GlobalContext] User detected/changed, triggering data load...');
            loadEmployees();

            const currentSelectedId = localStorage.getItem('SearchUser') || user.emp_id;
            setSelectedEmpId(currentSelectedId);

            if (currentSelectedId) {
                loadDevices(currentSelectedId, false);
            }
        } else {
            setEmpList([]);
            setDeviceList([]);
        }
    }, [user, loadEmployees, loadDevices]);

    // ─── Ensure Valid Selected Employee in Scoped List ─────────────────────────
    useEffect(() => {
        if (empList && empList.length > 0) {
            const isValid = empList.some(
                item => String(item.emp_id) === String(selectedEmpId) || String(item.id) === String(selectedEmpId)
            );
            if (!isValid || !selectedEmpId) {
                const currentUser = getUser();
                const selfInList = empList.find(
                    item => currentUser && (
                        String(item.emp_id) === String(currentUser.emp_id) ||
                        String(item.id) === String(currentUser.emp_id) ||
                        String(item.id) === String(currentUser.id)
                    )
                );
                const defaultEmp = selfInList || empList[0];
                const newId = defaultEmp.emp_id || String(defaultEmp.id);
                console.log('[GlobalContext] Auto-selecting valid employee from scoped list:', newId, defaultEmp.name);
                handleGlobalUserChange(newId);
            }
        }
    }, [empList]);

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleGlobalUserChange = (empId) => {
        const idStr = String(empId);
        setSelectedEmpId(idStr);
        localStorage.setItem('SearchUser', idStr);
        setSelectedDeviceId('0');
        localStorage.setItem('dev_id', '0');
        loadDevices(idStr, true);   // reset device for new employee
    };

    const handleGlobalDeviceChange = (devId) => {
        setSelectedDeviceId(devId);
        if (devId !== '0') {
            localStorage.setItem('dev_id', devId);
        }
    };

    return (
        <GlobalContext.Provider value={{
            empList,
            deviceList,
            empLoading,
            deviceLoading,
            selectedEmpId,
            selectedDeviceId,
            handleGlobalUserChange,
            handleGlobalDeviceChange,
            loadEmployees,
            user
        }}>
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobalContext = () => useContext(GlobalContext);

export const SelectedEmpContext = () => {
    const { empList, empLoading, selectedEmpId, handleGlobalUserChange } = useGlobalContext();
    const [localPlaceholder, setLocalPlaceholder] = useState('Search employee...');

    // When empList is loaded or selectedEmpId changes, re-evaluate placeholder
    useEffect(() => {
        if (empLoading) {
            setLocalPlaceholder('Loading...');
        } else if (empList && empList.length > 0) {
            const found = empList.find(
                item => String(item.emp_id) === String(selectedEmpId) ||
                        String(item.id) === String(selectedEmpId) ||
                        String(item.EmployeeCode) === String(selectedEmpId) ||
                        String(item.EmployeeId) === String(selectedEmpId)
            );
            if (found) {
                setLocalPlaceholder(found.name || found.EmployeeName || found.emp_name);
            } else {
                setLocalPlaceholder('Search employee...');
            }
        }
    }, [empList, empLoading, selectedEmpId]);

    return (
        <EmpDropdowndash
            placeholder={localPlaceholder}
            data={empList}
            handleChange={handleGlobalUserChange}
            selectedId={selectedEmpId}
        />
    );
};

export const SelectedDeviceContext = () => {
    const { deviceList, deviceLoading, selectedDeviceId, handleGlobalDeviceChange } = useGlobalContext();
    return (
        <div className="dropdown w-100">
            <button
                className="btn btn-drop-down dropdown-toggle w-100 d-flex justify-content-between align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                <span>
                    {deviceLoading
                        ? 'Loading...'
                        : (selectedDeviceId !== '0' ? selectedDeviceId?.slice(0, 12) : 'All Devices')}
                </span>
            </button>
            <ul className="dropdown-menu w-100">
                <li>
                    <a className="dropdown-item" onClick={() => handleGlobalDeviceChange('0')}>
                        All Devices
                    </a>
                </li>
                {Array.isArray(deviceList) && deviceList.map((option) => (
                    <li key={option.device_id}>
                        <a
                            className="dropdown-item"
                            href="#"
                            onClick={(e) => { e.preventDefault(); handleGlobalDeviceChange(option.device_id); }}
                        >
                            {option.device_id}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};
